import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, DropdownMenu, IconButton, TextField } from "@deere/isg.component-library";
import {
  IconChevronLeft,
  IconCircleClose,
  IconDelete,
  IconEdit,
  IconLand,
  IconRefresh,
  IconSave,
} from "@deere/icons/new";
import "./PropertiesPanel.css";
import { useMap } from "@vis.gl/react-google-maps";

const backendURI = process.env.REACT_APP_STRIPPED_BACKEND_URI;

interface PolylineProps {
  points: string;
  stroke?: string;
  strokeWidth?: number;
}

const Polyline: React.FC<PolylineProps> = ({
  points,
  stroke = "black",
  strokeWidth = 2,
}) => {
  return (
    <svg width="32" height="32" viewBox="0 0 200 200">
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
    </svg>
  );
};

interface PolygonProps {
  points: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
}

const Polygon: React.FC<PolygonProps> = ({
  points,
  fill = "lightblue",
  stroke = "black",
  strokeWidth = 1,
}) => {
  return (
    <svg width="32" height="32" viewBox="0 0 200 200">
      <polygon points={points} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
    </svg>
  );
};

/** Helper to get a human-friendly color for a type badge */
function getTypeBadgeStyle(objectType: string, typeValue: string): { bg: string; text: string; border: string } {
  const typeKey = (typeValue || "").toUpperCase();
  switch (typeKey) {
    case "FREE_SPACE_ZONE":
      return { bg: "#e8f5e9", text: "#2e7d32", border: "#a5d6a7" };
    case "RESTRICTED_ZONE":
      return { bg: "#ffebee", text: "#c62828", border: "#ef9a9a" };
    case "BOUNDARY":
      return { bg: "#fff8e1", text: "#f57f17", border: "#ffe082" };
    case "OI_OBJECT":
      return { bg: "#e3f2fd", text: "#1565c0", border: "#90caf9" };
    case "WORK_ZONE":
      return { bg: "#fff3e0", text: "#e65100", border: "#ffcc80" };
    case "NAVIGABLE_PATH":
    case "JOBSITE_PATH":
      return { bg: "#e8f5e9", text: "#2e7d32", border: "#a5d6a7" };
    case "WINDROW":
      return { bg: "#f3e5f5", text: "#7b1fa2", border: "#ce93d8" };
    case "STATIC_ROUTE":
      return { bg: "#e0f7fa", text: "#00695c", border: "#80cbc4" };
    case "VEHICLE_PATH":
      return { bg: "#e8eaf6", text: "#283593", border: "#9fa8da" };
    default:
      return { bg: "#f5f5f5", text: "#616161", border: "#e0e0e0" };
  }
}

/** Format a type string for display: "FREE_SPACE_ZONE" → "Free Space Zone" */
function formatTypeLabel(raw: string): string {
  if (!raw) return "Unknown";
  return raw
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

interface PropertiesPanelProps {
  currentTopicString: any;
  selectedProperty: any;
  setSelectedProperty: (object: any) => void;
  handleClose: () => void;
  changeFirstPanel: () => void;
  selectedFeatureId: string | null;
  setSelectedFeatureId: (id: string | null) => void;
}

function PropertiesPanel({
  currentTopicString,
  selectedProperty,
  setSelectedProperty,
  handleClose,
  changeFirstPanel,
  selectedFeatureId,
  setSelectedFeatureId,
}: PropertiesPanelProps) {
  const [propertyName, setPropertyName] = useState(selectedProperty?.name || "");
  const [propertyUUID, setPropertyUUID] = useState(selectedProperty?.object?.uuid || "999999999");
  const [propertyType, setPropertyType] = useState(selectedProperty?.type || "");
  const [propertyDescription, setPropertyDescription] = useState(
    selectedProperty?.object?.description || "Property Description",
  );
  const [propertyTypeCaps, setPropertyTypeCaps] = useState(
    selectedProperty?.object?.pathType || "",
  );
  const [zoneOptions, setZoneOptions] = useState(["Zone 1", "Zone 2", "Zone 3"]);
  const [lineOptions, setLineOptions] = useState(["Line 1", "Line 2", "Line 3"]);
  // const [selectedOption, setSelectedOption] = useState(0);
  const [selectedOptionString, setSelectedOptionString] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [points, setPoints] = useState<google.maps.LatLng[]>([]);
  const [selectedOption, setSelectedOption] = useState("");
  const pointsRef = useRef(points); // Create a ref to hold the latest points
  const [alertVisibleAllFields, setAlertVisibleAllFields] = useState(false);
  const map = useMap("mainMap"); // Get the map instance

  const [propertyTypeEnum, setPropertyTypeEnum] = useState(0);
  const enumMap: { [key: string]: number } = {
    "Free Space Zone": 0,
    "Restricted Zone": 1,
    Boundary: 2,
    "OI Object": 3,
    "Work Zone": 4,
    "Navigable Path": 0,
    Windrow: 1,
    "Static Route": 2,
  };

  async function handleZoneSelection(zoneString: string, zoneEnum: any) {
    // console.log(zoneString);
    setSelectedOption(zoneEnum);
    setSelectedOptionString(zoneString);
  }

  const zoneItems = [
    {
      // Boundary
      onClick: () => handleZoneSelection("Boundary", 2),
      id: "2",
      title: "Boundary",
      subtitle: "An defined area",
      Icon: (
        <Polygon
          points="50,50 150,20 200,100 150,180 50,150"
          stroke="#FFD700" // Gold
          strokeWidth={4}
          fill="#FFFACD" // Light Yellow
        />
      ),
    },
    {
      // Free Space Zone
      onClick: () => handleZoneSelection("Free Space Zone", 0),
      id: "0",
      title: "Free Space Zone",
      subtitle: "An easily navigatable area",
      Icon: (
        <Polygon
          points="50,50 150,20 200,100 150,180 50,150"
          stroke="#32CD32" // Lime Green
          strokeWidth={4}
          fill="#90EE90" // Light Green
        />
      ),
    },
    {
      // Work Zone
      onClick: () => handleZoneSelection("Work Zone", 4),
      id: "4",
      title: "Work Zone",
      subtitle: "Typical operation area",
      Icon: (
        <Polygon
          points="50,50 150,20 200,100 150,180 50,150"
          stroke="#FF8C00" // Dark Orange
          strokeWidth={6}
          fill="#FFE4B5" // Lemon Chiffon
        />
      ),
    },
    {
      // Restricted Zone
      onClick: () => handleZoneSelection("Restricted Zone", 1),
      id: "1",
      title: "Restricted Zone",
      subtitle: "An area to never traverse",
      Icon: (
        <Polygon
          points="50,50 150,20 200,100 150,180 50,150"
          stroke="#FF4500" // Orange Red
          strokeWidth={6}
          fill="#FFCCCB" // Light Red
        />
      ),
    },
    {
      // OI Object
      onClick: () => handleZoneSelection("OI Object", 3),
      id: "3",
      title: "OI Object",
      subtitle: "An identified obstacle",
      Icon: (
        <Polygon
          points="50,50 150,20 200,100 150,180 50,150"
          stroke="#1E90FF" // Dodger Blue
          strokeWidth={6}
          fill="#ADD8E6" // Light Blue
        />
      ),
    },
  ];

  // ['Navigable Path', 'Windrow', 'Static Route']
  const lineItems = [
    {
      // Navigable Path
      onClick: () => handleZoneSelection("Navigable Path", 0),
      id: "0",
      title: "Navigable Path",
      subtitle: "A path that can be followed",
      Icon: (
        <Polyline
          points="50,150 100,100 150,130 200,80"
          stroke="#32CD32" // Lime Green
          strokeWidth={4}
        />
      ),
    },
    {
      // Windrow
      onClick: () => handleZoneSelection("Windrow", 1),
      id: "1",
      title: "Windrow",
      subtitle: "A pass taken by a grading machine",
      Icon: (
        <Polyline
          points="50,150 100,100 150,130 200,80"
          stroke="#FF4500" // Orange Red
          strokeWidth={4}
        />
      ),
    },
    {
      // Static Route
      onClick: () => handleZoneSelection("Static Route", 2),
      id: "2",
      title: "Static Route",
      subtitle: "A route that will not change",
      Icon: (
        <Polyline
          points="50,150 100,100 150,130 200,80"
          stroke="#FFD700" // Gold
          strokeWidth={4}
        />
      ),
    },
  ];

  // console.log(selectedProperty);

  useEffect(() => {
    if (selectedProperty) {
      setPropertyName(selectedProperty.item.title);
      setPropertyType(selectedProperty.item.subtitle);
      setPropertyTypeCaps(selectedProperty.object.zoneType);
      setPropertyUUID(selectedProperty.object?.uuid || "999999999");
      setPropertyDescription(selectedProperty.object?.description || "Property Description");
      // Set the selected feature ID for highlighting, strip curly braces if present
      const featureId = selectedProperty.object?.uuid || selectedProperty.item?.id;
      if (setSelectedFeatureId && featureId) {
        const cleanId = String(featureId).replace(/[{}]/g, "");
        setSelectedFeatureId(cleanId);
      }
    }
  }, [selectedProperty]);

  // const handleSave = () => {
  //     if (selectedProperty?.object?.objectType === 'Shape') {
  //         publishNewZone();
  //     }else if (selectedProperty?.object?.objectType === 'Line') {

  //     }else {
  //         console.error("Error saving shape");
  //     }
  //     // const updatedProperty = {
  //     //     ...selectedProperty,
  //     //     name: propertyName,
  //     //     type: propertyType,
  //     //     option: selectedOption
  //     // };
  //     // setSelectedProperty(updatedProperty);
  //     // Additional save logic can go here (e.g., API call)

  // };

  async function handleSave() {
    console.log("I am saving");
    setIsSaving(true);

    console.log("propertyName: " + propertyName);
    console.log("propertyDescription: " + propertyDescription);

    if (points && propertyName !== "" && propertyDescription !== "" && selectedOption !== "") {
      async function savePointsZone(pointsArray: any) {
        // const url = 'http://localhost/API/Save/Boundary'; // Replace with your actual endpoint
        const url = "https://" + backendURI + "/Save/BoundaryNew";

        console.log("propertyTypeEnum: " + propertyTypeEnum);

        try {
          const response = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json", // Specify that the content type is JSON
            },
            body: JSON.stringify({
              // pointsArray: pointsArray,
              // zoneType: propertyTypeStringCaps.toString(),
              // // currentTopicString: currentTopicString + "Shape/" + Math.floor(Math.random() * 99999999).toString()
              // currentTopicString: currentTopicString + "Shape/" + propertyName.toString(),

              zoneType: propertyTypeEnum,
              pointsArray: pointsArray,
              currentTopicString: currentTopicString + "Shape/" + propertyName.toString(),
              deleted: false,
              description: propertyDescription,
            }), // Convert the pointsArray to JSON
          });

          // Check if the response is successful
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const data = await response.json(); // Parse the JSON response
          console.log("Points saved successfully:", data);
        } catch (error) {
          console.error("Error saving points:", error);
        }
      }

      async function savePointsLine(pointsArray: any) {
        // const url = 'http://localhost/API/Save/Line'; // Replace with your actual endpoint
        const url = "https://" + backendURI + "/Save/LineNew";

        console.log(
          JSON.stringify({
            // pointsArray: pointsArray,
            // zoneType: propertyTypeEnum,
            // // currentTopicString: currentTopicString + "Line/" + Math.floor(Math.random() * 99999999).toString()
            // currentTopicString: currentTopicString + "Line/" + propertyName.toString(),
            zoneType: propertyTypeEnum,
            pointsArray: pointsArray,
            currentTopicString: currentTopicString + "Line/" + propertyName.toString(),
            deleted: false,
            description: propertyDescription,
          }),
        );

        try {
          const response = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json", // Specify that the content type is JSON
            },
            body: JSON.stringify({
              // pointsArray: pointsArray,
              // zoneType: propertyTypeEnum,
              // // currentTopicString: currentTopicString + "Line/" + Math.floor(Math.random() * 99999999).toString()
              // currentTopicString: currentTopicString + "Line/" + propertyName.toString(),
              zoneType: propertyTypeEnum,
              pointsArray: pointsArray,
              currentTopicString: currentTopicString + "Line/" + propertyName.toString(),
              deleted: false,
              description: propertyDescription,
            }), // Convert the pointsArray to JSON
          });

          // Check if the response is successful
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const data = await response.json(); // Parse the JSON response
          console.log("Points saved successfully:", data);
        } catch (error) {
          console.error("Error saving points:", error);
        }
      }

      const latestPoints = pointsRef.current; // Access latest points from ref
      console.log(latestPoints);
      if (latestPoints.length > 0) {
        // createNewBoundary(points); // Call the function to create a new boundary with the points
        if (propertyType === "Zone") {
          await savePointsZone(latestPoints);
        } else if (propertyType === "Line") {
          await savePointsLine(latestPoints);
        } else {
          console.log("ERROR: Improper Property Type");
        }
      } else {
        console.log("No points to save.");
      }

      clearTemporaryPointsAndLines();
      // Remove buttons from the map
      // google.maps.event.removeListener(clickListener);
      setIsSaving(false);
      handleLocalClose();
    } else {
      setAlertVisibleAllFields(true);
      setIsSaving(false);
    }
  }

  async function handleLocalClose() {
    console.log("Boundary creation canceled!");
    setPoints([]); // Clear the points

    handleClose();
  }

  function clearTemporaryPointsAndLines() {
    if (map) {
      let tempFeature = map.data.getFeatureById("temporaryBoundaryID");
      if (tempFeature) {
        tempFeature.setGeometry(new google.maps.Data.LineString([]));
      }
    }
  }

  async function publishNewZone() {
    // const currentTopicString: string = "orchestratorORG/JobSite/Session/";
    const propertyZoneType: any = selectedOption;
    // FREE_SPACE_ZONE = 0; // RESTRICTED_ZONE = 1; // BOUNDARY = 2; // OI_OBJECT  = 3; // WORK_ZONE = 4;
    const pointsArray: any[] = selectedProperty?.object?.points;
    const propertyName: string = "Engineering Tool Zone";
    const propertyDescription: string = "This is a description of the Zone";
    try {
      const response = await fetch(`/API/Save/BoundaryNew`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          zoneType: propertyZoneType,
          pointsArray: pointsArray,
          currentTopicString: currentTopicString + "Shape/" + propertyName.toString(),
          deleted: false,
          description: propertyDescription,
        }),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Handle response if necessary
      console.log(`Successfully sent:`, await response.text());
    } catch (error) {
      console.error("Error sending request:", error);
    }
  }

  // Determine if creating new property (fields are empty/default)
  const isNewProperty = !selectedProperty || !selectedProperty.item || !selectedProperty.item.title;

  const objectType = selectedProperty?.object?.objectType || "";
  const rawType = selectedProperty?.object?.zoneType || selectedProperty?.object?.pathType || selectedProperty?.object?.pointType || "";
  const badgeStyle = getTypeBadgeStyle(objectType, rawType);
  const pointCount = selectedProperty?.object?.points?.length ?? 0;

  return (
    <div className="PropertiesPanel">
      {/* Header */}
      <div className="pp-header">
        <IconButton
          icon={<IconChevronLeft />}
          variant="borderless"
          onClick={handleClose}
          style={{ width: "32px", height: "32px", flexShrink: 0 }}
        />
        <span className="pp-header-title">{propertyName || "Attribute"}</span>
        <IconButton
          icon={<IconCircleClose size={18} />}
          variant="borderless"
          onClick={handleClose}
          style={{ flexShrink: 0 }}
        />
      </div>

      {alertVisibleAllFields && (
        <Alert onClose={() => setAlertVisibleAllFields(false)} severity={"warning"}>
          {"There are settings conflicts."}
        </Alert>
      )}

      {/* Shape preview */}
      {selectedProperty?.item?.Icon && (
        <div className="pp-preview">
          <div className="pp-preview-inner">
            {selectedProperty.item.Icon}
          </div>
        </div>
      )}

      {/* Type badge */}
      {!isNewProperty && rawType && (
        <div className="pp-badge-row">
          <span
            className="pp-type-badge"
            style={{
              backgroundColor: badgeStyle.bg,
              color: badgeStyle.text,
              border: `1px solid ${badgeStyle.border}`,
            }}
          >
            {objectType === "Line" ? "Line" : objectType === "Point" ? "Point" : "Zone"} &middot; {formatTypeLabel(rawType)}
          </span>
        </div>
      )}

      {/* Info cards — read-only display */}
      {!isNewProperty && (
        <div className="pp-info-section">
          <div className="pp-info-card">
            <div className="pp-info-row">
              <span className="pp-info-label">Name</span>
              <span className="pp-info-value">{propertyName || "—"}</span>
            </div>
            <div className="pp-info-divider" />
            <div className="pp-info-row">
              <span className="pp-info-label">Description</span>
              <span className="pp-info-value">{propertyDescription || "—"}</span>
            </div>
            <div className="pp-info-divider" />
            <div className="pp-info-row">
              <span className="pp-info-label">Type</span>
              <span className="pp-info-value">{formatTypeLabel(rawType)}</span>
            </div>
            {objectType !== "Point" && (
              <>
                <div className="pp-info-divider" />
                <div className="pp-info-row">
                  <span className="pp-info-label">Points</span>
                  <span className="pp-info-value">{pointCount}</span>
                </div>
              </>
            )}
          </div>

          <div className="pp-info-card pp-info-card--secondary">
            <div className="pp-info-row">
              <span className="pp-info-label">UUID</span>
              <span className="pp-info-value pp-info-mono">{propertyUUID}</span>
            </div>
          </div>
        </div>
      )}

      {/* New property creation flow — kept intact */}
      {isNewProperty && (
        <div className="pp-create-section">
          <div className="textFields">
            <TextField
              label="Attribute Name"
              onChange={(event: any) => setPropertyName(event.target.value)}
              placeholder="Enter Name"
              value={propertyName}
              style={{ width: "100%", padding: "8px 0" }}
            />
            <TextField
              label="Description"
              onChange={(event: any) => setPropertyDescription(event.target.value)}
              placeholder="Enter Description"
              value={propertyDescription}
              style={{ width: "100%", padding: "8px 0" }}
            />
          </div>
          {selectedProperty?.object?.objectType === "Shape" && (
            <div className="dropdownContainer">
              <div className="SectionTitle2">Zone Type</div>
              <DropdownMenu
                buttonTitle={selectedOptionString || "Select Zone Option"}
                items={zoneItems}
              />
              <br />
              <div className="SectionTitle2">Primary Jobsite Boundary</div>
              <Button
                startIcon={<IconLand size={20} />}
                style={{ marginLeft: "6px", marginRight: "6px", color: "black", border: "none" }}
                className="SaveMissionButton"
                disabled={true}
              >
                Set Jobsite Boundary
              </Button>
            </div>
          )}
          {selectedProperty?.object?.objectType === "Line" && (
            <div className="dropdownContainer">
              <div className="SectionTitle2">Line Type</div>
              <DropdownMenu
                buttonTitle={selectedOptionString || "Select Line Option"}
                items={lineItems}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PropertiesPanel;
